import { useOrder } from "src/context/OrderProvider/useOrder";
import WhiteCard from "../WhiteCard";
import {
  FormControl,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BillingModel from "../BillingModel";
import OrderStatusChip from "../OrderStatusChip";
import { useCallback, useMemo, useState } from "react";
import ContentCopy from "../ContentCopy";
import { Cancel, Update } from "@mui/icons-material";
import { useUpdateOrderMutation } from "src/api/services/ordersApi";

interface Props {
  onSuccess: () => Promise<void>;
}

const productToForm: Record<string, string> = {
  ec: "pack(s)",
};

const Order = ({ onSuccess }: Props) => {
  const id = useOrder((o) => o.id);
  const pharmacy = useOrder((o) => o.pharmacy.name);
  const billingModel = useOrder((o) => o.product.billing_model);
  const status = useOrder((o) => o.status);
  const quantitiy = useOrder((o) => o.prescription.dispense_quantity);
  const medicationName = useOrder((o) => o.product.display_name);
  const productSlug = useOrder((o) => o.product.slug);
  const pharmacyOrderId = useOrder((o) => o.pharmacy_order_id);
  const trackingNumber = useOrder((o) => o.tracking_number);
  const [updateOrder] = useUpdateOrderMutation();
  const [edit, setEdit] = useState<boolean>(false);
  const [newTrackingNumber, setNewTrackingNumber] = useState<string>("");

  const form = useMemo(() => productToForm[productSlug] ?? null, [productSlug]);

  const handleTrackingNumber = useCallback(
    async (orderId: number) => {
      if (!orderId) return null;
      try {
        await updateOrder({
          id: orderId,
          tracking_number: newTrackingNumber,
        }).unwrap();
        setEdit(false);
        onSuccess();
      } catch (err) {
        console.error("Error updating tracking number", err);
      }
    },
    [newTrackingNumber, onSuccess, updateOrder],
  );

  return (
    <WhiteCard width="100%" sx={{ background: "white" }}>
      <Stack gap={1}>
        <Stack direction="row" gap={1} flexWrap="wrap" alignContent="center">
          <Typography fontWeight={700} variant="h5">{`#${id}`}</Typography>
          <Typography fontWeight={700} variant="h5">{`${pharmacy}`}</Typography>
          <BillingModel model={billingModel} />
          <OrderStatusChip status={status} />
        </Stack>
        <Stack direction={"row"} gap={2} alignItems={"center"}>
          <Typography fontWeight={700}>
            Pharmacy order #: {pharmacyOrderId}
          </Typography>
          {pharmacyOrderId ? (
            <ContentCopy text={pharmacyOrderId} contentType="orderNumber" />
          ) : null}
        </Stack>
        <Stack gap={1}>
          <Typography
            component={"em"}
            fontWeight={600}
          >{`Medications:`}</Typography>
          <Stack direction={"row"}>
            <Typography ml={1}>{medicationName}</Typography>
            {quantitiy ? (
              <Typography ml={1}>- {`${quantitiy} ${form}`}</Typography>
            ) : null}
          </Stack>
        </Stack>
        {trackingNumber && !edit ? (
          <Stack direction="row" alignItems="center">
            <Typography>Tracking #: {trackingNumber}</Typography>
            <IconButton size="small" onClick={() => setEdit((e) => !e)}>
              <Update />
            </IconButton>
          </Stack>
        ) : (
          <Stack direction="row">
            <FormControl fullWidth>
              <TextField
                placeholder="Tracking number"
                size="small"
                value={trackingNumber}
                onChange={(e) => setNewTrackingNumber(e.target.value)}
              />
            </FormControl>
            <IconButton size="small" onClick={() => handleTrackingNumber(id)}>
              <Update />
            </IconButton>
            <IconButton size="small" onClick={() => setEdit((e) => !e)}>
              <Cancel />
            </IconButton>
          </Stack>
        )}
      </Stack>
    </WhiteCard>
  );
};

export default Order;
